import React, { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import { useSelector } from 'react-redux'; 
import { useTheme } from '@mui/material';

const VALID_YEARS = ['2021', '2022', '2023', '2024', '2025'];

const VolumeProfile = () => {
  const theme = useTheme();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const pocSeriesRef = useRef(null);
  const vahSeriesRef = useRef(null);
  const valSeriesRef = useRef(null);
  const dataRef = useRef([]);
  const csrfToken = useSelector((state) => state.csrfToken);

  const [symbolInput, setSymbolInput] = useState('S50');

  // UI state
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedInterval, setSelectedInterval] = useState('');
  const [showPoc, setShowPoc] = useState(true);
  const [showVah, setShowVah] = useState(true);
  const [showVal, setShowVal] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const [pocColor, setPocColor] = useState('#ff0000');
  const [vahColor, setVahColor] = useState('#0000ff');
  const [valColor, setValColor] = useState('#00ff00');

  // toggle visibility
  const togglePoc = () => {
    setShowPoc(v => {
      pocSeriesRef.current.applyOptions({ visible: !v, color: pocColor });
      return !v;
    });
  };
  const toggleVah = () => {
    setShowVah(v => {
      vahSeriesRef.current.applyOptions({ visible: !v, color: vahColor });
      return !v;
    });
  };
  const toggleVal = () => {
    setShowVal(v => {
      valSeriesRef.current.applyOptions({ visible: !v, color: valColor });
      return !v;
    });
  };

  const onPickPoc   = e => {
    const c = e.target.value;
    setPocColor(c);
    pocSeriesRef.current.applyOptions({ color: c });
  };

  const onPickVah   = e => {
    const c = e.target.value;
    setVahColor(c);
    vahSeriesRef.current.applyOptions({ color: c });
  };

  const onPickVal   = e => {
    const c = e.target.value;
    setValColor(c);
    valSeriesRef.current.applyOptions({ color: c });
  };

  // Zoom controls
  const handleShowAll = () => {
    if (!chartRef.current || !dataRef.current.length) return;
    chartRef.current.timeScale().fitContent();

  };

  const handlePresent = () => {
    const chart = chartRef.current;
    if (!chart || !dataRef.current.length) return;
  
    // 1) compute the “window” you want to show (same as before)
    const times = dataRef.current.map(c => c.time);
    const fullTo = Math.max(...times);
    const visible = chart.timeScale().getVisibleRange();
    const windowLen = visible.to - visible.from;
    chart.timeScale().setVisibleRange({
      from: fullTo - windowLen,
      to: fullTo,
    });
  
    // 2) bump the rightOffset so you get that 20-px gap on the right
    chart.applyOptions({
      timeScale: {
        rightOffset: 20,
      },
    });
  };
  
  // Helper: Parse a timestamp string like "2021.08.27 09:45" into Unix time (seconds) adjusted to GMT+7.
  const parseTimestamp = (ts) => {
    ts = ts.trim().replace(/^\ufeff/, '');
    const [datePart, timePart] = ts.split(' ');
    const fixedDate = datePart.split('.').join('-');
    const newTs = `${fixedDate} ${timePart}`;
    const t = new Date(newTs);
    // Add 7 hours (7*3600 seconds) to adjust to GMT+7.
    return Math.floor((t.getTime() + 7 * 3600 * 1000) / 1000);
  };

  // Convert JSON rows into candlestick data.
  // Both minute and tick aggregated files have the same schema.
  const processAggregatedData = (fetchedData) => {
    const candles = fetchedData.map((row) => ({
      time: parseTimestamp(row.Timestamp),
      open: parseFloat(row.Open),
      high: parseFloat(row.High),
      low: parseFloat(row.Low),
      close: parseFloat(row.Close),
      volume: parseInt(row.Ticks, 10),
    }));
    candles.sort((a, b) => a.time - b.time);
    // Ensure strictly increasing timestamps.
    for (let i = 1; i < candles.length; i++) {
      if (candles[i].time <= candles[i - 1].time) {
        candles[i].time = candles[i - 1].time + 1;
      }
    }
    return candles;
  };

  // Compute a simple volume profile (POC, VAH, VAL) from a set of candles.
  const calculateVolumeProfile = (candles, binSize = 0.5, valueAreaPercent = 0.7) => {
    const minPrice = Math.min(...candles.map(c => c.low));
    const maxPrice = Math.max(...candles.map(c => c.high));
    const binCount = Math.ceil((maxPrice - minPrice) / binSize);
    const bins = new Array(binCount).fill(0);
    candles.forEach(candle => {
      const startBin = Math.floor((candle.low - minPrice) / binSize);
      const endBin = Math.floor((candle.high - minPrice) / binSize);
      const binsCovered = endBin - startBin + 1;
      const volumePerBin = candle.volume / binsCovered;
      for (let i = startBin; i <= endBin; i++) {
        bins[i] += volumePerBin;
      }
    });
    const pocIndex = bins.reduce(
      (maxIdx, vol, idx, arr) => (vol > arr[maxIdx] ? idx : maxIdx),
      0
    );
    const totalVolume = bins.reduce((sum, vol) => sum + vol, 0);
    let cumulativeVolume = bins[pocIndex];
    const includedBins = new Set([pocIndex]);
    let left = pocIndex - 1;
    let right = pocIndex + 1;
    while (
      cumulativeVolume < totalVolume * valueAreaPercent &&
      (left >= 0 || right < bins.length)
    ) {
      const leftVolume = left >= 0 ? bins[left] : -1;
      const rightVolume = right < bins.length ? bins[right] : -1;
      if (leftVolume >= rightVolume) {
        if (left >= 0) {
          includedBins.add(left);
          cumulativeVolume += bins[left];
          left--;
        } else if (right < bins.length) {
          includedBins.add(right);
          cumulativeVolume += bins[right];
          right++;
        }
      } else {
        if (right < bins.length) {
          includedBins.add(right);
          cumulativeVolume += bins[right];
          right++;
        } else if (left >= 0) {
          includedBins.add(left);
          cumulativeVolume += bins[left];
          left--;
        }
      }
    }
    const sortedBins = Array.from(includedBins).sort((a, b) => a - b);
    const valBin = sortedBins[0];
    const vahBin = sortedBins[sortedBins.length - 1];
    const pocPrice = minPrice + (pocIndex + 0.5) * binSize;
    const valPrice = minPrice + valBin * binSize;
    const vahPrice = minPrice + (vahBin + 1) * binSize;
    return { poc: pocPrice, val: valPrice, vah: vahPrice };
  };

  // Group candlestick data by day (using YYYY-MM-DD).
  const groupDataByDay = (candles) => {
    return candles.reduce((acc, c) => {
      const dateStr = new Date(c.time * 1000).toISOString().split('T')[0];
      if (!acc[dateStr]) acc[dateStr] = [];
      acc[dateStr].push(c);
      return acc;
    }, {});
  };

  // New helpers at top of file:
  function localTimeAggregate(data, freqMinutes) {
    // data: array of { time, open, high, low, close, volume } at 1-minute resolution
    const groups = {};
    data.forEach(bar => {
      // floor to nearest freqMinutes
      const floored = bar.time - (bar.time % (freqMinutes*60));
      if (!groups[floored]) groups[floored] = [];
      groups[floored].push(bar);
    });
    return Object.entries(groups)
      .sort((a,b)=>a[0]-b[0])
      .map(([ts, bars]) => {
        const opens = bars[0].open;
        const closes= bars[bars.length-1].close;
        const highs = Math.max(...bars.map(b=>b.high));
        const lows  = Math.min(...bars.map(b=>b.low));
        const ticks = bars.reduce((sum,b)=>sum+ b.volume,0);
        return { time: Number(ts), value: closes, open:opens, high:highs, low:lows, close:closes, volume:ticks };
      });
  }

  function localTickAggregate(data, tickThreshold) {
    // data: array of { time, open, high, low, close, volume } from 500-tick bars
    const rows = [];
    let buffer = [], cum = 0;
    data.forEach(bar => {
      buffer.push(bar);
      cum += bar.volume;
      if (cum >= tickThreshold) {
        const opens = buffer[0].open;
        const highs = Math.max(...buffer.map(b=>b.high));
        const lows  = Math.min(...buffer.map(b=>b.low));
        const closes= buffer[buffer.length-1].close;
        const ticks = buffer.reduce((s,b)=>s+b.volume,0);
        rows.push({ 
          time: buffer[buffer.length - 1].time,
          open:opens, high:highs, low:lows, close:closes, volume:ticks 
        });
        buffer = []; cum = 0;
      }
    });
    return rows;
  }

  // Fetch JSON from the server and update the chart.
  // If "All" is selected as year, fetch for every year and combine the results.
  // const fetchAndUpdateChart = async () => {
  //   if (!selectedYear || !selectedInterval) {
  //     console.warn('Year or aggregation not selected.');
  //     return;
  //   }
  //   try {
  //     let combinedData = [];
  //     if (selectedYear === "All") {
  //       // Fetch data for all years in parallel.
  //       // const requests = VALID_YEARS.map(year =>
  //       //   fetch(`http://172.17.1.85:5001/api/aggregated/${selectedInterval}/${year}`)
  //       // );
  //       const requests = VALID_YEARS.map(year =>
  //         fetch(
  //           `${window.config.base_api_url}/api/aggregated/${selectedInterval}/${year}`,
  //           {
  //             headers: {
  //               'Content-Type': 'application/json',
  //               'xsrf-token': csrfToken,
  //             },
  //           }
  //         )
  //       );
  //       const responses = await Promise.all(requests);
  //       const jsonArrays = await Promise.all(responses.map(res => res.json()));
  //       jsonArrays.forEach(jsonData => {
  //         combinedData = combinedData.concat(jsonData);
  //       });
  //     } else {
  //       // const response = await fetch(
  //       //   `http://172.17.1.85:5001/api/aggregated/${selectedInterval}/${selectedYear}`
  //       // );
  //       const response = await fetch(
  //           `${window.config.base_api_url}/api/aggregated/${selectedInterval}/${selectedYear}`,
  //           {
  //             headers: {
  //               'Content-Type': 'application/json',
  //               'xsrf-token': csrfToken,
  //             },
  //           }
  //         );
  //       combinedData = await response.json();
  //     }

  //     const candles = processAggregatedData(combinedData);
  //     dataRef.current = candles;
  //     if (candleSeriesRef.current) {
  //       candleSeriesRef.current.setData(candles);
  //     }
  const fetchAndUpdateChart = async () => {
      if (!selectedYear || !selectedInterval) return;
    
      // if the user chose “All”, we want to fetch every year in VALID_YEARS
      const years = selectedYear === 'All'
        ? VALID_YEARS
        : [selectedYear];
      const sym = symbolInput.trim() || 'S50';
    
      // for non-1min/500tick intervals we still fetch the base bars (1min or 500tick)
      const baseInterval = selectedInterval.endsWith('min') && selectedInterval !== '1min'
        ? '1min'
        : (selectedInterval.endsWith('tick') && selectedInterval !== '500tick'
          ? '500tick'
          : selectedInterval
        );
    
      // build one request per year, branching on symbolInput
      const urls = years.map(y => {
        if (sym.toUpperCase() === 'S50') {
          return `${window.config.base_api_url}/api/aggregated/${baseInterval}/${y}`;
        } else {
          // other symbols include symbol in the path
          return `${window.config.base_api_url}/api/aggregated/${encodeURIComponent(sym)}/${baseInterval}/${y}`;
        }
      });
    
      // fire them all in parallel
      const responses = await Promise.all(
        urls.map(u => fetch(u, {
          headers: {
            'Content-Type': 'application/json',
            'xsrf-token': csrfToken,
          },
        }))
      );
      try {
        let combinedData = [];
        for (let res of responses) {
          const json = await res.json();
          combinedData = combinedData.concat(json);
        }
      
        // parse into “bars”
        const bars = combinedData.map(r => ({
          time: parseTimestamp(r.Timestamp),
          open: parseFloat(r.Open),
          high: parseFloat(r.High),
          low:  parseFloat(r.Low),
          close:parseFloat(r.Close),
          volume: parseInt(r.Ticks,10),
        })).sort((a,b)=>a.time-b.time);
      
        let finalBars = bars;
      
        // if we need to re-aggregate:
        if (selectedInterval.endsWith('min') && selectedInterval!=='1min') {
          const freq = parseInt(selectedInterval,10);           // e.g. "5min" → 5
          finalBars = localTimeAggregate(bars, freq);
        }
        if (selectedInterval.endsWith('tick') && selectedInterval!=='500tick') {
          const thresh = parseInt(selectedInterval,10);         // e.g. "1500tick" → 1500
          finalBars = localTickAggregate(bars, thresh);
        }
      
        // now set data on your chart

        // enforce strictly‐increasing timestamps
        for (let i = 1; i < finalBars.length; i++) {
          if (finalBars[i].time <= finalBars[i - 1].time) {
            finalBars[i].time = finalBars[i - 1].time + 1;
          }
        }
    
        // now set data on your chart
        dataRef.current = finalBars;
        candleSeriesRef.current.setData(finalBars);
        const grouped = groupDataByDay(finalBars);
        const dayBoundaries = [];
        const pocData = [];
        const vahData = [];
        const valData = [];
        Object.keys(grouped).forEach((date) => {
          const dayCandles = grouped[date];
          dayCandles.sort((a, b) => a.time - b.time);
          const { poc, val, vah } = calculateVolumeProfile(dayCandles, 0.5, 0.7);
          const dayStart = dayCandles[0].time;
          let dayEnd = dayCandles[dayCandles.length - 1].time;
          if (dayStart === dayEnd) dayEnd = dayStart + 1;
          dayBoundaries.push(dayStart);
          pocData.push({ time: dayStart, value: poc });
          pocData.push({ time: dayEnd, value: poc });
          vahData.push({ time: dayStart, value: vah });
          vahData.push({ time: dayEnd, value: vah });
          valData.push({ time: dayStart, value: val });
          valData.push({ time: dayEnd, value: val });
          console.log(
            `Date: ${date} => POC: ${poc.toFixed(2)}, VAH: ${vah.toFixed(2)}, VAL: ${val.toFixed(2)}`
          );
        });
        if (pocSeriesRef.current) pocSeriesRef.current.setData(pocData);
        if (vahSeriesRef.current) vahSeriesRef.current.setData(vahData);
        if (valSeriesRef.current) valSeriesRef.current.setData(valData);

        const addVerticalSeparators = () => {
          const container = chartContainerRef.current;
          const existing = container.querySelectorAll('.day-separator');
          existing.forEach((el) => el.remove());
          dayBoundaries.forEach((timestamp, idx) => {
            if (idx === 0) return;
            const x = chartRef.current.timeScale().timeToCoordinate(timestamp);
            if (x !== null && x !== undefined) {
              const separator = document.createElement('div');
              separator.className = 'day-separator';
              separator.style.position = 'absolute';
              separator.style.top = '0';
              separator.style.height = '100%';
              separator.style.width = '2px';
              separator.style.backgroundColor = 'darkbrown';
              separator.style.left = `${x}px`;
              container.appendChild(separator);
            }
          });
        };
        setTimeout(addVerticalSeparators, 300);
    } catch (err) {
      console.error("Error fetching data: ", err);
    }
  };

  useEffect(() => {
    // only poll when we're looking at 2025 (or "All" which covers 2025)
    if (selectedYear === '2025' || selectedYear === 'All') {
      // fetch immediately, then every 30s
      fetchAndUpdateChart();
      const id = setInterval(fetchAndUpdateChart, 30_000);
      return () => clearInterval(id);
    }
    // if they switch away from 2025/All, we automatically clear the poll
  }, [selectedYear, selectedInterval]);
  
  useEffect(() => {
    // initialize the chart with the darker theme
    chartRef.current = createChart(chartContainerRef.current, {
      layout: {
        attributionLogo: false,
        background: { type: 'solid', color: theme.palette.background.default },
        textColor: theme.palette.text.primary,
      },
                     grid: {
         horzLines: { color: theme.palette.divider },
         vertLines: { color: theme.palette.divider },
      },
      timeScale: {
        timeVisible: true,
        borderColor: '#323856',
        rightOffset: 20,
      },
      priceScale: {
        borderColor: '#323856',
      },
      height: chartContainerRef.current.clientHeight,
      width: chartContainerRef.current.clientWidth,
    });

    candleSeriesRef.current = chartRef.current.addCandlestickSeries({
      upColor: '#4bffb5',
      downColor: '#ff4976',
      wickUpColor: '#4bffb5',
      wickDownColor: '#ff4976',
    });
    pocSeriesRef.current = chartRef.current.addLineSeries({
      color: pocColor,
      lineWidth: 2,
      lineStyle: 2,
      visible: showPoc,
    });
    vahSeriesRef.current = chartRef.current.addLineSeries({
      color: vahColor,
      lineWidth: 2,
      lineStyle: 2,
      visible: showVah,
    });
    valSeriesRef.current = chartRef.current.addLineSeries({
      color: valColor,
      lineWidth: 2,
      lineStyle: 2,
      visible: showVal,
    });

    fetchAndUpdateChart();
    window.addEventListener('resize', () => {
      chartRef.current.applyOptions({
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
      });
      fetchAndUpdateChart();
    });
    return () => chartRef.current.remove();
  }, []);

  useEffect(() => {
    if (chartRef.current) fetchAndUpdateChart();
  }, [selectedYear, selectedInterval]);

  return (
    <div style={{
      fontFamily: 'Lato, Sans‑serif',
      color: '#FFFFFF',
      padding: '20px',
    }}>
      <h2 style={{ textAlign: 'center', margin: '0 0 20px' }}>Volume Profile</h2>

      {/* controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
      }}>
        {/* --- new Symbol input --- */}
        <div>
          <label style={{ marginRight: 8 }}>Symbol:</label>
          <input
            type="text"
            value={symbolInput}
            onChange={e => setSymbolInput(e.target.value)}
            placeholder="e.g. S50"
            style={{
              fontFamily: 'Lato, Sans-serif',
              padding: '4px 8px',
              borderRadius: '5px',
              border: '1px solid #323856',
              background: '#575b69',
              color: '#fff',
              width: '80px',
            }}
          />
        </div>
        <div>
          <label style={{ marginRight: 8 }}>Year:</label>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            style={{
              fontFamily: 'Lato, Sans‑serif',
              padding: '4px 8px',
              borderRadius: '5px',
              border: '1px solid #323856',
              background: '#575b69',
              color: '#fff',
            }}
          >
            <option value="">-- Select Year --</option>
            <option value="All">All Years</option>
            {VALID_YEARS.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ marginRight: 8 }}>Time Frame:</label>
          <select
            value={selectedInterval}
            onChange={e => setSelectedInterval(e.target.value)}
            style={{
              fontFamily: 'Lato, Sans‑serif',
              padding: '4px 8px',
              borderRadius: '5px',
              border: '1px solid #323856',
              background: '#575b69',
              color: '#fff',
            }}
          >
            <option value="">-- Select --</option>
            <optgroup label="Minute">
              {['1min','2min','3min','5min','10min','15min','30min','45min']
                .map(v => <option key={v} value={v}>{v}</option>)}
            </optgroup>
            <optgroup label="Tick">
              {['500tick','1000tick','1500tick','2000tick','2500tick',
                '3000tick','3500tick','4000tick','4500tick','5000tick']
                .map(v => <option key={v} value={v}>{v}</option>)}
            </optgroup>
          </select>
        </div>
      </div>

      {/* chart & buttons */}
      <div style={{
        position: 'relative',
        width: '80vw',
        height: '80vh',
        margin: '0 auto',
        outline: '2px solid #fff',
        borderRadius: '10px',
        overflow: 'hidden',
        background: '#575b69',
      }}>
        <div style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10,
          pointerEvents: 'auto', // ensure it can receive clicks
          display: 'flex',
          gap: 8,
        }}>
          <button
            onClick={handleShowAll}
            style={{
              fontFamily: 'Lato, Sans‑serif',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            Show All
          </button>
          <button
            onClick={handlePresent}
            style={{
              fontFamily: 'Lato, Sans‑serif',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            Present
          </button>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{
                fontFamily: 'Lato, Sans‑serif',
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Hide/Show
            </button>
            {menuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%', right: 0,
                background: '#333',
                border: '1px solid #444',
                borderRadius: 4,
                padding: 8,
              }}>
                <label style={{ display: 'flex', alignItems: 'center', color: '#fff', marginBottom:4 }}>
                  <input type="checkbox" checked={showPoc} onChange={togglePoc} />{' '}
                  POC
                  <input
                    type="color"
                    value={pocColor}
                    onChange={onPickPoc}
                    style={{
                      marginLeft: 8,
                      width: 20,
                      height: 20,
                      padding: 0,
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', color: '#fff', marginBottom:4 }}>
                  <input type="checkbox" checked={showVah} onChange={toggleVah} />{' '}
                   VAH
                  <input
                    type="color"
                    value={vahColor}
                    onChange={onPickVah}
                    style={{
                      marginLeft: 8,
                      width: 20,
                      height: 20,
                      padding: 0,
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                  />
                </label>

                <label style={{ display: 'flex', alignItems: 'center', color: '#fff' }}>
                  <input type="checkbox" checked={showVal} onChange={toggleVal} />{' '}
                  VAL
                  <input
                    type="color"
                    value={valColor}
                    onChange={onPickVal}
                    style={{
                      marginLeft: 8,
                      width: 20,
                      height: 20,
                      padding: 0,
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* <div
          ref={chartContainerRef}
          style={{ width: '100%', height: '100%' }}
        /> */}
        <div
          ref={chartContainerRef}
          style={{
            position: 'relative', // make it positioned
            zIndex: 1,            // send it behind the controls
            width: '100%',
            height: '100%',
          }}
        />
      </div>
    </div>
  );
};

export default VolumeProfile;